/**
 * String utility functions for text manipulation and comparison
 */

export class StringUtil {
  /**
   * Calculate similarity between two strings using Levenshtein distance
   * Returns a value between 0 and 1, where 1 means identical
   */
  static calculateSimilarity(str1: string, str2: string): number {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;

    if (longer.length === 0) return 1.0;

    const editDistance = this.levenshteinDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  }

  /**
   * Calculate Levenshtein distance between two strings
   * Returns the minimum number of edits needed to transform one string into another
   */
  static levenshteinDistance(str1: string, str2: string): number {
    const matrix: number[][] = [];

    // Initialize matrix
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }

    // Fill matrix
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1, // substitution
            matrix[i][j - 1] + 1, // insertion
            matrix[i - 1][j] + 1, // deletion
          );
        }
      }
    }

    return matrix[str2.length][str1.length];
  }

  /**
   * Check if a string is a variation of another (contains, starts with, ends with)
   */
  static isVariation(original: string, candidate: string): boolean {
    const normalized = original.trim().toLowerCase();
    const candidateLower = candidate.toLowerCase();

    return (
      candidateLower.startsWith(normalized) ||
      candidateLower.endsWith(normalized) ||
      candidateLower.includes(normalized)
    );
  }

  /**
   * Normalize string for comparison (trim, uppercase, remove special chars)
   */
  static normalize(str: string): string {
    return str
      .trim()
      .toUpperCase()
      .replace(/[^\w\s]/gi, '');
  }

  /**
   * Truncate string to specified length with ellipsis
   */
  static truncate(str: string, maxLength: number): string {
    if (str.length <= maxLength) return str;
    return str.substring(0, maxLength - 3) + '...';
  }

  /**
   * Check if string contains any of the given terms (case-insensitive)
   */
  static containsAny(str: string, terms: string[]): boolean {
    const lowerStr = str.toLowerCase();
    return terms.some((term) => lowerStr.includes(term.toLowerCase()));
  }

  /**
   * Calculate match score for ranking suggestions
   */
  static calculateMatchScore(
    userInput: string,
    candidate: string,
  ): {
    score: number;
    matchType: 'exact' | 'starts' | 'contains' | 'similar';
  } {
    const normalizedInput = userInput.trim().toUpperCase();
    const normalizedCandidate = candidate.toUpperCase();

    if (normalizedInput === normalizedCandidate) {
      return { score: 1.0, matchType: 'exact' };
    }

    const startsWithInput = normalizedCandidate.startsWith(normalizedInput);
    const containsInput = normalizedCandidate.includes(normalizedInput);
    const similarity = this.calculateSimilarity(normalizedInput, normalizedCandidate);

    let score = similarity;
    let matchType: 'exact' | 'starts' | 'contains' | 'similar' = 'similar';

    if (startsWithInput) {
      score += 0.5;
      matchType = 'starts';
    } else if (containsInput) {
      score += 0.3;
      matchType = 'contains';
    }

    return { score, matchType };
  }
}
